import MontuMarkdown from "@repo/components/src/shared/markdown";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import { ProductsSelectionQuery } from "@repo/graphql/documents/product/query.generated";
import { Package } from "lucide-react-native";
import React from "react";
import type { ViewStyle } from "react-native";

type ProductNode =
  ProductsSelectionQuery["product"]["list"]["edges"][number]["node"];

type Props = {
  product: ProductNode;
  index: number;
};

/** `animationDelay` is web-runtime-only — same carve-out `ThumbnailDeck`'s own
 * delay helper takes. Staggers the row popping in once the products section
 * flips from "writing" to real content, instead of all rows landing at once. */
const delayStyle = (index: number): ViewStyle =>
  ({ animationDelay: `${index * 70}ms` }) as ViewStyle;

/** Ported from `apps/mobil/.../about/ProductsCard.tsx`'s per-product row. */
const ProductCard = React.memo(({ product, index }: Props) => (
  <Box
    key={product.id}
    style={delayStyle(index)}
    className="rounded-3xl bg-white overflow-hidden border border-outline-100 animate-fade-rise motion-reduce:animate-none"
  >
    <HStack space="md" className="px-4 py-4">
      <Box className="rounded-xl overflow-hidden items-center justify-center w-[72px] h-[72px] bg-background-100">
        {product.image?.url ? (
          <Image
            source={{ uri: product.image.url }}
            alt={product.name}
            className="w-full h-full"
            contentFit="cover"
          />
        ) : (
          <Icon as={Package} size="lg" className="text-typography-400" />
        )}
      </Box>
      <VStack className="flex-1" space="xs">
        <Text
          bold
          size="2xs"
          className="uppercase tracking-widest text-typography-500"
        >
          {`Producto ${index + 1}`}
        </Text>
        <Text
          bold
          className="text-typography-950 text-base tracking-tight"
          numberOfLines={2}
        >
          {product.name}
        </Text>
        {Number(product.price?.amount ?? 0) > 0 ? (
          <Text size="xs" className="text-typography-600">
            Valor referencial · {formatMoneyFromFragment(product.price)}
          </Text>
        ) : null}
      </VStack>
    </HStack>
    {product.description?.trim() ? (
      <Box className="px-4 py-3 border-t border-outline-50">
        <MontuMarkdown baseSize={13}>{product.description}</MontuMarkdown>
      </Box>
    ) : null}
  </Box>
));

ProductCard.displayName = "ProductCard";

export default ProductCard;
